import type { Pane } from "tweakpane";
import { ICON_1, ICON_2, ICON_3, ICON_4 } from "../icons";
import { createSimulation } from "../simulation/simulation";
import { definePresets, type Preset, type Simulation } from "../simulation/types";

// TODO: Tune params so that we're not creating too many bins (esp. for logo).

const PRESETS_BY_NAME: Readonly<Record<string, Preset | undefined>> = definePresets({
  home: {
    defaults: {
      worldMode: "viewport",
      dotSize: 3.5,
      pointerRadius: 20,
      pointerRadiusDown: 80,
      pointerForce: 80,
      pointerForceDown: 40,
      returnForce: 1,
      returnDamping: 1,
      dt: 0.05,
      mu_k: 4.0,
      sigma_k: 1.0,
      mu_g: 0.73,
      sigma_g: 0.18,
      c_rep: 2.0,
      r_rep: 1.0,
      resetAttempts: 4,
      initialFieldScattering: 0,
    },
    frames: [
      { id: "home-icon-1", particles: ICON_1 },
      { id: "home-icon-2", particles: ICON_2 },
      { id: "home-icon-3", particles: ICON_3 },
      { id: "home-icon-4", particles: ICON_4 },
    ],
  },
  error404: {
    defaults: {
      fieldUrl: "/static/404.png",
      fieldFit: "contain",
      viewFit: "contain",
      worldWidth: 200,
      worldHeight: 200,
      pointerRadius: 3,
      pointerRadiusDown: 10,
      pointerForce: 10,
      pointerForceDown: 20,
      returnForce: 0,
      returnDamping: 0.5,
      dt: 0.05,
      mu_k: 4.0,
      sigma_k: 1.0,
      mu_g: 0.73,
      sigma_g: 0.18,
      c_rep: 2.0,
      r_rep: 1.0,
      resetAttempts: 100,
      initialFieldScattering: 0,
    },
    breakpoints: [
      {
        minWidth: 0,
        particleCount: 128 * 60,
        dotSize: 0.4,
      },
      {
        minWidth: 768,
        particleCount: 196 * 160,
        dotSize: 0.2,
      },
      {
        minWidth: 1280,
        particleCount: 256 * 160,
        dotSize: 0.2,
      },
    ],
  },
  error500: {
    defaults: {
      fieldUrl: "/static/500.png",
      fieldFit: "contain",
      viewFit: "contain",
      worldWidth: 16 * 200 / 16,
      worldHeight: 10 * 200 / 16,
      dotSize: 0.2,
      pointerRadius: 3,
      pointerRadiusDown: 10,
      pointerForce: 10,
      pointerForceDown: 20,
      returnForce: 0,
      returnDamping: 0.5,
      dt: 0.05,
      mu_k: 4.0,
      sigma_k: 1.0,
      mu_g: 0.73,
      sigma_g: 0.18,
      c_rep: 2.0,
      r_rep: 1.0,
      resetAttempts: 100,
      initialFieldScattering: 0,
    },
    breakpoints: [
      {
        minWidth: 1280,
        particleCount: 256 * 160,
      },
      {
        minWidth: 0,
        particleCount: 256 * 60,
      },
    ],
  },
  logo: {
    defaults: {
      fieldUrl: "/static/field.png",
      particleCount: 64 * 64,
      worldWidth: 230,
      worldHeight: 174,
      dotSize: 1,
      pointerRadius: 10,
      pointerRadiusDown: 20,
      pointerForce: 10,
      pointerForceDown: 20,
      returnForce: 1,
      returnDamping: 0.5,
      dt: 0.05,
      mu_k: 4.0,
      sigma_k: 1.0,
      mu_g: 0.73,
      sigma_g: 0.18,
      c_rep: 2.0,
      r_rep: 2.5,
      resetAttempts: 8,
      initialFieldScattering: 0.65,
    },
  },
  approach: {
    defaults: {
      fieldUrl: "/static/gradient1.png",
      pointerRadius: 3,
      pointerRadiusDown: 10,
      pointerForce: 10,
      pointerForceDown: 20,
      returnForce: 0,
      returnDamping: 0.5,
      dt: 0.05,
      mu_k: 4.0,
      sigma_k: 1.0,
      mu_g: 0.73,
      sigma_g: 0.18,
      c_rep: 2.0,
      r_rep: 1.0,
      resetAttempts: 5,
      initialFieldScattering: 0,
    },
    breakpoints: [
      {
        minWidth: 0,
        worldWidth: 4 * 200 / 4,
        worldHeight: 5 * 200 / 4,
        particleCount: 196 * 60,
        dotSize: 0.4,
      },
      {
        minWidth: 768,
        worldWidth: 16 * 200 / 16,
        worldHeight: 10 * 200 / 16,
        particleCount: 256 * 160,
        dotSize: 0.2,
      },
      {
        minWidth: 1280,
        particleCount: 256 * 160,
      },
    ],
  },
});

class ParticleSimulationElement extends HTMLElement {
  sim: Simulation | null = null;

  parentContainer: HTMLElement | null = null;
  parentObserver: MutationObserver | null = null;

  paneContainer: HTMLElement | null = null;
  pane: Pane | null = null;

  connectedCallback() {
    const isDebug = this.hasAttribute("debug");

    const presetName = this.getAttribute("preset");
    if (!presetName) return;

    const preset = PRESETS_BY_NAME[presetName];
    if (!preset) return;

    this.sim = createSimulation({ container: this, preset });

    this.parentContainer = this.closest("morph-container");
    if (this.parentContainer) {
      this.parentObserver = new MutationObserver(() => this.syncWithParent());
      this.parentObserver.observe(this.parentContainer, { attributes: true, attributeFilter: ["open"] });
    }

    this.sim.start().then((success) => {
      if (!success) {
        document.documentElement.classList.add("no-webgpu");
        return;
      }

      this.syncWithParent();

      // TODO: Handle component disconnected while setting up TweakPane.
      if (isDebug) {
        requestAnimationFrame(() => {
          import("tweakpane").then(({ Pane }) => {
            const sim = this.sim;
            if (!sim) return;

            const container = document.body.appendChild(document.createElement("div"));
            container.style.position = "fixed";
            container.style.inset = "var(--header-height) 16px auto auto";
            container.style.zIndex = "10000";

            const pane = new Pane({ container });

            pane.addBinding(sim.params, "dt");
            pane.addBinding(sim.params, "mu_k");
            pane.addBinding(sim.params, "sigma_k");
            pane.addBinding(sim.params, "mu_g");
            pane.addBinding(sim.params, "sigma_g");
            pane.addBinding(sim.params, "c_rep");
            pane.addBinding(sim.params, "r_rep");
            pane.addBinding(sim.params, "returnForce");
            pane.addBinding(sim.params, "returnDamping");

            pane.addButton({ title: "Reset" }).on("click", () => { sim.reset(); });

            this.paneContainer = container;
            this.pane = pane;
          });
        });
      }
    });
  }

  disconnectedCallback() {
    this.pane?.dispose();
    this.pane = null;
    this.paneContainer?.remove();
    this.paneContainer = null;

    this.parentObserver?.disconnect();
    this.parentObserver = null;
    this.parentContainer = null;

    this.sim?.stop();
    this.sim = null;
  }

  syncWithParent() {
    if (!this.sim || !this.parentContainer) return;

    if (this.parentContainer.hasAttribute("open")) {
      // TODO: Don't reset all resources, just particle positions.
      this.sim.reset();
      this.sim.resume();
    } else {
      this.sim.pause();
    }
  }
}

customElements.define("particle-simulation", ParticleSimulationElement);
